import { useMutation, useQuery } from "@apollo/client";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type {
  CreateSalonReviewInput,
  SalonReviewListInput,
  SalonReviewSort,
  SalonReviewSource,
  UpdateSalonReviewInput,
} from "@/gql/graphql";
import {
  CREATE_SALON_REVIEW_MUTATION,
  DELETE_SALON_REVIEW_MUTATION,
  SALON_REVIEWS_QUERY,
  UPDATE_SALON_REVIEW_MUTATION,
} from "@/graphql/salon-reviews";
import { useAsyncAction } from "@/lib/hooks/use-async-action";
import { useSalonStore } from "@/lib/store/salon";
import { useReviewUIStore } from "../store/use-review-ui-store";

/**
 * Page size matches the backend default (`SALON_REVIEW_PAGE_DEFAULT_LIMIT`).
 * Keep them in sync — the backend clamps to `[1, 100]` but the UX is tuned
 * for 20-per-page "Load more" cadence.
 */
const PAGE_SIZE = 20;

/**
 * Options for narrowing the review list to a specific surface. `source`
 * filters server-side so pages stay consistent — passing `source: "GOOGLE"`
 * yields the GPA list, `source: "MANUAL"` yields the Platform list, and
 * omitting it returns the combined feed (used on the overview "Last
 * reviews" preview).
 *
 * `limitOverride` lets callers like the overview preview ask for a smaller
 * page than the default 20 — useful when we only need the top few without
 * paying for a full page.
 */
export interface UseSalonReviewsOptions {
  source?: SalonReviewSource;
  limitOverride?: number;
  /**
   * When true, the hook ignores `starFilter`/`replyFilter` from the UI
   * store. The Reviews overview "Last reviews" preview uses this so a
   * filter set on a detail page doesn't leak into the unfiltered preview.
   */
  ignoreFilters?: boolean;
  /**
   * Optional server-side sort. Defaults to NEWEST on the backend when
   * omitted; the "Awaiting reply" triage card passes LOWEST_FIRST so the
   * most painful unanswered reviews surface first.
   */
  sort?: SalonReviewSort;
  /**
   * When true, `unrepliedOnly: true` is sent to the backend regardless of
   * the UI store's reply filter. Lets callers (e.g. the Awaiting Reply
   * card) demand the unreplied set without coupling to the detail-page
   * filter store.
   */
  unrepliedOnly?: boolean;
  /**
   * Upper bound for star rating (inclusive). When set, only reviews with
   * starRating ≤ this value are returned. Used by the Awaiting Reply card
   * to show only 1–3★ reviews.
   */
  maxStarRating?: number | null;
}

export function useSalonReviews(options: UseSalonReviewsOptions = {}) {
  const {
    source,
    limitOverride,
    ignoreFilters = false,
    sort,
    unrepliedOnly: unrepliedOverride,
    maxStarRating,
  } = options;
  const { t } = useTranslation("settings");
  const { currentSalon } = useSalonStore();
  const salonId = currentSalon?.id;

  // Filters live in the UI store so the strip/dropdowns can mutate them
  // independently — but they're applied server-side now so the page boundary
  // stays consistent with the filter (no "empty page" UX).
  const { starFilter, replyFilter } = useReviewUIStore();
  const effectiveStarFilter = ignoreFilters ? null : starFilter;
  const effectiveReplyFilter = ignoreFilters ? "all" : replyFilter;

  // Memoize so `loadMore`'s dependency array stays stable across renders —
  // otherwise the callback rebuilds every render and useExhaustiveDependencies
  // (correctly) complains.
  const baseInput = useMemo<SalonReviewListInput>(
    () => ({
      limit: limitOverride ?? PAGE_SIZE,
      starRating: effectiveStarFilter,
      // Explicit override wins; otherwise fall back to the UI store's
      // reply filter. The override exists so the Awaiting Reply triage
      // doesn't need to mutate the shared store just to fetch its data.
      unrepliedOnly:
        typeof unrepliedOverride === "boolean"
          ? unrepliedOverride
          : effectiveReplyFilter === "unreplied",
      maxStarRating: maxStarRating ?? null,
      source: source ?? null,
      sort: sort ?? null,
    }),
    [
      effectiveStarFilter,
      effectiveReplyFilter,
      source,
      limitOverride,
      sort,
      unrepliedOverride,
      maxStarRating,
    ]
  );

  const { data, loading, error, refetch, fetchMore, networkStatus } = useQuery(
    SALON_REVIEWS_QUERY,
    {
      variables: { salonId: salonId ?? "", input: baseInput },
      skip: !salonId,
      errorPolicy: "all",
      notifyOnNetworkStatusChange: true,
    }
  );

  const page = data?.salonReviews;
  const reviews = page?.nodes ?? [];
  const hasMore = page?.hasMore ?? false;
  const nextCursor = page?.nextCursor ?? null;

  const loadMore = useCallback(async () => {
    if (!salonId || !hasMore || !nextCursor) return;
    await fetchMore({
      variables: {
        salonId,
        input: { ...baseInput, cursor: nextCursor },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.salonReviews) return prev;
        return {
          ...prev,
          salonReviews: {
            ...fetchMoreResult.salonReviews,
            // Append — never prepend. The server orders newest-first and
            // returns older rows on each subsequent page.
            nodes: [...prev.salonReviews.nodes, ...fetchMoreResult.salonReviews.nodes],
          },
        };
      },
    });
  }, [salonId, hasMore, nextCursor, fetchMore, baseInput]);

  const [createMutation] = useMutation(CREATE_SALON_REVIEW_MUTATION);
  const [updateMutation] = useMutation(UPDATE_SALON_REVIEW_MUTATION);
  const [deleteMutation] = useMutation(DELETE_SALON_REVIEW_MUTATION);

  // Refetches re-execute with the CURRENT variables — which means page 1 of
  // the current filter set. The user loses scroll-load state but always sees
  // their write reflected at the top. Acceptable trade-off for a list where
  // mutations are infrequent.
  const createReview = useAsyncAction(
    async (input: Omit<CreateSalonReviewInput, "salonId">) => {
      if (!salonId) throw new Error("No salon selected");
      const result = await createMutation({
        variables: { input: { ...input, salonId } },
        refetchQueries: ["SalonReviews"],
      });
      return result.data?.createSalonReview;
    },
    {
      loadingMessage: t("reviews.toast.creating"),
      successMessage: t("reviews.toast.created"),
      errorMessage: t("reviews.toast.createFailed"),
    }
  );

  const updateReview = useAsyncAction(
    async (id: string, input: UpdateSalonReviewInput) => {
      if (!salonId) throw new Error("No salon selected");
      const result = await updateMutation({
        variables: { salonId, id, input },
        refetchQueries: ["SalonReviews"],
      });
      return result.data?.updateSalonReview;
    },
    {
      loadingMessage: t("reviews.toast.updating"),
      successMessage: t("reviews.toast.updated"),
      errorMessage: t("reviews.toast.updateFailed"),
    }
  );

  const deleteReview = useAsyncAction(
    async (id: string) => {
      if (!salonId) throw new Error("No salon selected");
      const result = await deleteMutation({
        variables: { salonId, id },
        refetchQueries: ["SalonReviews"],
      });
      return result.data?.deleteSalonReview;
    },
    {
      loadingMessage: t("reviews.toast.deleting"),
      successMessage: t("reviews.toast.deleted"),
      errorMessage: t("reviews.toast.deleteFailed"),
    }
  );

  return {
    reviews,
    loading,
    error,
    refetch,
    hasMore,
    loadMore,
    // 4 = NetworkStatus.fetchMore in Apollo — exposing the int avoids pulling
    // the enum import for one comparison.
    loadingMore: networkStatus === 4,
    createReview: createReview.execute,
    creating: createReview.isLoading,
    updateReview: updateReview.execute,
    updating: updateReview.isLoading,
    deleteReview: deleteReview.execute,
    deleting: deleteReview.isLoading,
  };
}
